import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { NavLink } from "react-router";
import type { ReactNode } from "react";

interface SidebarNavItemProps {
  to: string;
  icon: ReactNode;
  label: string;
  badge?: string;
  badgeColor?: "default" | "success";
  end?: boolean;
  collapsed: boolean;
}

export function SidebarNavItem({
  to,
  icon,
  label,
  badge,
  badgeColor = "default",
  end = false,
  collapsed,
}: SidebarNavItemProps) {
  return (
    <Tooltip title={collapsed ? label : ""} placement="right" arrow disableInteractive>
      <NavLink
        to={to}
        end={end}
        aria-label={label}
        style={{ textDecoration: "none", display: "block" }}
      >
        {({ isActive }) => (
          <Box
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              py: 0.875,
              px: collapsed ? 0 : 1.5,
              justifyContent: collapsed ? "center" : "flex-start",
              borderLeft: `3px solid ${isActive ? theme.palette.primary.main : "transparent"}`,
              backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.1) : "transparent",
              color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
              transition: "background-color 0.15s, color 0.15s",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: isActive
                  ? alpha(theme.palette.primary.main, 0.13)
                  : alpha(theme.palette.common.white, 0.05),
                color: theme.palette.text.primary,
              },
            })}
          >
            <Box
              aria-hidden="true"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "inherit",
                "& svg": { fontSize: "1.125rem" },
              }}
            >
              {icon}
            </Box>
            {!collapsed && (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: "inherit",
                    flexGrow: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </Typography>
                {badge != null && (
                  <Chip
                    label={badge}
                    size="small"
                    sx={(theme) => ({
                      height: 18,
                      fontSize: "0.5625rem",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      borderRadius: "4px",
                      backgroundColor:
                        badgeColor === "success"
                          ? alpha(theme.palette.success.main, 0.15)
                          : alpha(theme.palette.text.secondary, 0.12),
                      color:
                        badgeColor === "success"
                          ? theme.palette.success.main
                          : theme.palette.text.secondary,
                      border: "none",
                      "& .MuiChip-label": { px: 0.75 },
                    })}
                  />
                )}
              </>
            )}
          </Box>
        )}
      </NavLink>
    </Tooltip>
  );
}
